import { useState, useEffect, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';

import { paths } from 'src/routes/paths';

import { SITE_ID, SiteIdEnum } from 'src/config-global';

import { SplashScreen } from 'src/components/loading-screen';

import type { ConnectivityRuntimeState } from './types';
import { useConnectivityStore } from './connectivity-store';
import { useOfflineShellAvailable } from './offline-shell-access';
import { useOfflinePaymentPolicy } from './offline-payment-policy';
import { ConnectionRecoveryPage } from './connection-recovery-page';
import { usePosTransitionSurfaceStore } from './pos-transition-store';
import { OfflineModeIndicatorScope } from './offline-mode-indicator-suppression';

type Props = { children: ReactNode };

const OFFLINE_SAFE_PATHS = [
  paths.dashboard.v3.pos,
  paths.dashboard.v4.pos,
  paths.dashboard.v4.restaurantPos,
];
const OFFLINE_SAFE_EXACT_PATHS = [paths.dashboard.root, paths.dashboard.appHub];

/**
 * Runtime states belonging to one live connection-loss sequence. The first
 * candidate state keeps the POS visible silently; the footer countdown starts once
 * OFFLINE_AVAILABLE confirms the outage and remains through a manual/final
 * RECOVERING check.
 */
const CONNECTION_LOSS_SEQUENCE_STATES: ReadonlySet<ConnectivityRuntimeState> =
  new Set<ConnectivityRuntimeState>(['CONNECTION_ISSUE', 'OFFLINE_AVAILABLE', 'RECOVERING']);

function isPathAtOrBelow(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export function ConnectivityBoundary({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const runtimeState = useConnectivityStore((state) => state.runtimeState);
  const issue = useConnectivityStore((state) => state.issue);
  const offlineShellAvailable = useOfflineShellAvailable();
  const offlinePaymentPolicy = useOfflinePaymentPolicy();
  const [hasEnteredApp, setHasEnteredApp] = useState(runtimeState === 'ONLINE');
  const isAppHubRoute = OFFLINE_SAFE_EXACT_PATHS.includes(location.pathname);
  const isPaymentRoute = isPathAtOrBelow(location.pathname, paths.dashboard.v4.payment);
  const paymentRouteParams = new URLSearchParams(location.search);
  const isQuickPayEntry = paymentRouteParams.get('quickPay') === 'true';
  const paymentEntrySupportsOfflineMode =
    paymentRouteParams.get('navigateBackTo') !== 'mobile-inventory-pos';
  const [hasMountedEligiblePaymentPage, setHasMountedEligiblePaymentPage] = useState(
    isPaymentRoute && runtimeState === 'ONLINE'
  );
  const isOfflineSafeStaticPath =
    OFFLINE_SAFE_EXACT_PATHS.includes(location.pathname) ||
    OFFLINE_SAFE_PATHS.some((path) => isPathAtOrBelow(location.pathname, path));
  const isOfflineSafe =
    isOfflineSafeStaticPath ||
    (isPaymentRoute &&
      (paymentEntrySupportsOfflineMode || hasMountedEligiblePaymentPage) &&
      (isQuickPayEntry || offlinePaymentPolicy.canEnterPaymentPage));
  const retainMountedPaymentPage =
    hasMountedEligiblePaymentPage &&
    isPaymentRoute &&
    offlinePaymentPolicy.canRetainMountedPaymentPage;
  const bypassRecoveryScreen =
    location.pathname.startsWith(`${paths.operationalApps.root}/`) ||
    location.pathname === paths.system.permissions ||
    location.pathname === paths.system.updateVersion;

  // Retain the shell only on a live ONLINE → lost transition.
  // Losses discovered at boot keep the startup/recovery flow, and an operator
  // deliberately leaving offline mode for the recovery page (showRecoveryPage)
  // must reach it rather than start another automatic offline transition.
  // Derived during render (not an effect) so the very first lost-state render
  // keeps the children mounted — an effect would let one eviction commit slip
  // through and reset the retained POS surface.
  const [connectionLossArmed, setConnectionLossArmed] = useState(false);
  const [lastSeenRuntimeState, setLastSeenRuntimeState] = useState(runtimeState);
  if (runtimeState !== lastSeenRuntimeState) {
    setLastSeenRuntimeState(runtimeState);
    if (!CONNECTION_LOSS_SEQUENCE_STATES.has(runtimeState)) {
      if (connectionLossArmed) setConnectionLossArmed(false);
    } else if (lastSeenRuntimeState === 'ONLINE') {
      setConnectionLossArmed(true);
    }
  }
  // Retention is a view-lifetime decision, not permission to accept money.
  // Keep the exact routed tree even when eligibility becomes unavailable.
  const liveSalePath =
    isPaymentRoute || OFFLINE_SAFE_PATHS.some((path) => isPathAtOrBelow(location.pathname, path));
  const [mountedSalePath, setMountedSalePath] = useState<string | null>(
    liveSalePath &&
      (runtimeState === 'ONLINE' ||
        (runtimeState === 'OFFLINE_MODE' && isOfflineSafe && offlineShellAvailable))
      ? location.pathname
      : null
  );
  if (mountedSalePath !== location.pathname && mountedSalePath !== null) setMountedSalePath(null);
  if (
    liveSalePath &&
    (runtimeState === 'ONLINE' ||
      (runtimeState === 'OFFLINE_MODE' && isOfflineSafe && offlineShellAvailable)) &&
    mountedSalePath !== location.pathname
  ) {
    setMountedSalePath(location.pathname);
  }
  const retainLiveSale =
    SITE_ID === SiteIdEnum.PointOfSale && mountedSalePath === location.pathname && liveSalePath;
  const retainLiveShell =
    SITE_ID === SiteIdEnum.PointOfSale &&
    hasEnteredApp &&
    offlineShellAvailable &&
    isOfflineSafeStaticPath;
  const keepConnectionIssueSurface =
    SITE_ID === SiteIdEnum.PointOfSale &&
    connectionLossArmed &&
    runtimeState === 'CONNECTION_ISSUE';

  useEffect(() => {
    usePosTransitionSurfaceStore.setState({ available: retainLiveSale || retainLiveShell });
    return () => usePosTransitionSurfaceStore.setState({ available: false });
  }, [retainLiveSale, retainLiveShell]);

  useEffect(() => {
    if (runtimeState === 'ONLINE') setHasEnteredApp(true);
    if (!isPaymentRoute) {
      setHasMountedEligiblePaymentPage(false);
    } else if (
      runtimeState === 'ONLINE' ||
      (runtimeState === 'OFFLINE_MODE' && isOfflineSafe && offlineShellAvailable)
    ) {
      setHasMountedEligiblePaymentPage(true);
    }
    if (runtimeState === 'OFFLINE_MODE' && !isOfflineSafe && !retainLiveSale) {
      sessionStorage.setItem(
        'preOfflineLocation',
        `${location.pathname}${location.search}${location.hash}`
      );
      return;
    }
    if (runtimeState === 'ONLINE') {
      const returnLocation = sessionStorage.getItem('preOfflineLocation');
      if (returnLocation) {
        sessionStorage.removeItem('preOfflineLocation');
        if (!retainLiveSale) navigate(returnLocation, { replace: true });
      }
    }
  }, [
    isOfflineSafe,
    retainLiveSale,
    isPaymentRoute,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    offlineShellAvailable,
    runtimeState,
  ]);

  const showChildren =
    runtimeState === 'ONLINE' ||
    bypassRecoveryScreen ||
    retainMountedPaymentPage ||
    keepConnectionIssueSurface ||
    retainLiveSale ||
    (retainLiveShell && connectionLossArmed);
  const showOfflineChildren =
    runtimeState === 'OFFLINE_MODE' && isOfflineSafe && offlineShellAvailable;
  // App Hub is the offline shell destination. Once it has rendered successfully,
  // keep that routed tree mounted behind recovery so Work offline reveals the
  // existing shell instead of rerunning updater, auth, version, and preload guards.
  const retainMountedAppHub = hasEnteredApp && offlineShellAvailable && isAppHubRoute;
  // Keep an already-mounted startup guard alive while the coordinator calls its
  // registered recovery task. Live sale retention above also covers device/API outages.
  const retainStartupGuard = hasEnteredApp && issue === 'startup';
  const showStartupScreen =
    !showChildren &&
    !showOfflineChildren &&
    (runtimeState === 'CHECKING' || runtimeState === 'CONNECTION_ISSUE');

  return (
    <OfflineModeIndicatorScope visible={showOfflineChildren}>
      {(showChildren || showOfflineChildren || retainMountedAppHub || retainStartupGuard) && (
        <Box sx={{ display: showChildren || showOfflineChildren ? 'contents' : 'none' }}>
          {children}
        </Box>
      )}
      {runtimeState === 'OFFLINE_MODE' &&
        offlineShellAvailable &&
        !isOfflineSafe &&
        !retainLiveSale &&
        !bypassRecoveryScreen && <Navigate to={paths.dashboard.appHub} replace />}
      {showStartupScreen && <SplashScreen />}
      {!showChildren &&
        !showOfflineChildren &&
        !showStartupScreen &&
        (runtimeState !== 'OFFLINE_MODE' || !offlineShellAvailable) && <ConnectionRecoveryPage />}
    </OfflineModeIndicatorScope>
  );
}
